// src/components/ui/Layout/Container.tsx
import React from 'react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Container.module.css';

export interface ContainerProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
  // Дополнительные свойства для адаптивности
  bottomNav?: boolean; // Учитывать наличие нижней навигации на мобильных
  fullHeight?: boolean; // Контейнер на всю высоту экрана
}

export const Container: React.FC<ContainerProps> = ({
  children,
  size = 'lg',
  padding = 'md',
  className = '',
  bottomNav = false,
  fullHeight = false
}) => {
  // Используем хук для определения типа устройства
  const { isMobile, isTablet } = useResponsive();
  
  // Получаем класс размера
  const getSizeClass = () => {
    // На мобильных всегда используем полную ширину
    if (isMobile) return styles.full;
    
    // На планшетах ограничиваем только большие размеры
    if (isTablet && (size === 'xl' || size === '2xl')) {
      return styles.lg;
    }
    
    // Стандартные значения
    switch (size) {
      case 'sm': return styles.sm;
      case 'md': return styles.md;
      case 'lg': return styles.lg;
      case 'xl': return styles.xl;
      case '2xl': return styles.xl2;
      case 'full': return styles.full;
      default: return styles.lg;
    }
  };
  
  // Получаем класс для отступов
  const getPaddingClass = () => {
    // Для мобильных устройств используем адаптивные отступы
    if (isMobile) {
      switch(padding) {
        case 'lg': return styles.mobilePaddingLg;
        case 'md': return styles.mobilePaddingMd;
        case 'sm': return styles.mobilePaddingSm;
        default: return styles.paddingNone;
      }
    }
    
    // Стандартные отступы
    switch (padding) {
      case 'none': return styles.paddingNone;
      case 'sm': return styles.paddingSm;
      case 'md': return styles.paddingMd;
      case 'lg': return styles.paddingLg;
      default: return styles.paddingMd;
    }
  };
  
  // Собираем все классы
  const containerClasses = [
    styles.container,
    getSizeClass(),
    getPaddingClass(),
    bottomNav && isMobile ? styles.bottomNavPadding : '',
    fullHeight ? styles.fullHeight : '',
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div className={containerClasses}>
      {children}
    </div>
  );
};

export default Container;